import type { ZoomCapabilities, ZoomConstraint } from "@/lib/browserCapabilities";
import { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useIsMobile } from "@/hooks/useMobile";
import {
  Camera,
  SwitchCamera,
  X,
  AlertCircle,
  Maximize2,
  Minimize2,
  Timer,
  ZoomIn,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { t } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import { Trans } from "@lingui/react/macro";

interface ModernCameraProps {
  onCapture: (imageData: string) => void;
  onClose?: () => void;
  isFullscreen?: boolean;
  onToggleFullscreen?: () => void;
  overlayGuide?: {
    width: string;
    height: string;
    shape?: "rectangle" | "circle" | "oval";
  };
  instructions?: {
    title: string;
    description: string;
    example?: string;
  };
  className?: string;
  /** Compact layout for modal dialogs: no nested card chrome, flip on preview, single capture row */
  dialogLayout?: boolean;
  autoCapture?: boolean;
  captureDelay?: number;
  disabled?: boolean;
  autoStart?: boolean; // Auto-start camera without showing initial screen
}

export function ModernCamera({
  onCapture,
  onClose,
  isFullscreen = false,
  onToggleFullscreen,
  instructions,
  className,
  dialogLayout = false,
  autoCapture = false,
  captureDelay = 3,
  disabled = false,
  autoStart = false,
}: ModernCameraProps) {
  const { i18n } = useLingui();
  const isMobile = useIsMobile();
  const [facingMode, setFacingMode] = useState<"user" | "environment">("user");
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [isCapturing, setIsCapturing] = useState(false);
  const [countdown, setCountdown] = useState<number | null>(null);
  const [hasStarted, setHasStarted] = useState(false);
  const [streamReady, setStreamReady] = useState(false);
  const [zoom, setZoom] = useState(1.0);
  const [zoomCapabilities, setZoomCapabilities] = useState<{
    min: number;
    max: number;
    supported: boolean;
  }>({ min: 1.0, max: 3.0, supported: false });

  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const countdownRef = useRef<NodeJS.Timeout | null>(null);
  const captureInProgressRef = useRef<boolean>(false);
  const isMountedRef = useRef(true);
  const startRequestIdRef = useRef(0);
  const touchStartDistanceRef = useRef<number | null>(null);
  const pinchStartZoomRef = useRef<number>(1.0);
  const lastZoomRef = useRef<number>(1.0);
  const zoomTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const stopActiveStream = useCallback(() => {
    if (!streamRef.current) return;
    streamRef.current.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setStreamReady(false);
  }, []);

  // Forward declarations
  const capturePhoto = useCallback(() => {
    if (!videoRef.current || !canvasRef.current || !streamReady || disabled) {
      console.warn(
        "Cannot capture: missing video or canvas ref, stream not ready, or disabled",
      );
      return;
    }

    // Use ref-based debouncing to prevent multiple rapid calls
    if (captureInProgressRef.current) {
      console.log("Capture already in progress, ignoring duplicate call");
      return;
    }

    captureInProgressRef.current = true;
    setIsCapturing(true);

    try {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");

      if (!ctx) throw new Error("Cannot get canvas context");

      // Set canvas size to video size
      canvas.width = video.videoWidth || 640;
      canvas.height = video.videoHeight || 480;

      // Draw video frame to canvas
      if (facingMode === "user") {
        // Flip horizontally for front camera
        ctx.save();
        ctx.scale(-1, 1);
        ctx.drawImage(video, -canvas.width, 0, canvas.width, canvas.height);
        ctx.restore();
      } else {
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      }

      // Convert to image data with maximum quality
      const imageData = canvas.toDataURL("image/jpeg", 1.0);
      onCapture(imageData);
    } catch (error) {
      console.error("Capture failed:", error);
      setCameraError("Failed to capture photo. Please try again.");
    } finally {
      setIsCapturing(false);
      captureInProgressRef.current = false;
    }
  }, [facingMode, onCapture, streamReady, disabled]);

  const startCountdown = useCallback(() => {
    if (countdownRef.current) clearTimeout(countdownRef.current);

    let count = captureDelay;
    setCountdown(count);

    const timer = setInterval(() => {
      count -= 1;
      setCountdown(count);
      if (count <= 0) {
        clearInterval(timer);
        setCountdown(null);
        capturePhoto();
      }
    }, 1000);

    countdownRef.current = timer;
  }, [captureDelay, capturePhoto]);

  // Handle capture action
  const handleCapture = useCallback(
    (e?: React.MouseEvent) => {
      e?.preventDefault();
      e?.stopPropagation();
      console.log("Capture handler triggered", {
        autoCapture,
        countdown,
        isCapturing,
        streamReady,
        captureInProgress: captureInProgressRef.current,
        disabled:
          isCapturing ||
          !!countdown ||
          !streamReady ||
          captureInProgressRef.current,
      });

      if (
        isCapturing ||
        countdown ||
        !streamReady ||
        captureInProgressRef.current
      ) {
        console.log("Capture blocked - button should be disabled");
        return;
      }

      if (autoCapture && !countdown) {
        console.log("Starting countdown...");
        startCountdown();
      } else if (!autoCapture && !countdown) {
        console.log("Taking photo directly...");
        capturePhoto();
      }
    },
    [
      autoCapture,
      countdown,
      startCountdown,
      capturePhoto,
      isCapturing,
      streamReady,
    ],
  );

  // Start camera function with enhanced stream management
  const startCamera = useCallback(async () => {
    const requestId = ++startRequestIdRef.current;
    console.log(
      "Starting camera... Fullscreen:",
      isFullscreen,
      "FacingMode:",
      facingMode,
    );
    setCameraError(null);
    setStreamReady(false);

    try {
      // Always stop existing stream when restarting
      stopActiveStream();

      // Check browser support
      if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error("Camera not supported in this browser");
      }

      // Enhanced constraints for highest quality
      const constraints = [
        {
          video: {
            facingMode: facingMode,
            width: { ideal: 1920 },
            height: { ideal: 1080 },
            frameRate: { ideal: 30 },
            // Prevent browser-side crop/scale behavior that can look "zoomed in"
            // on some mobile camera stacks.
            resizeMode: "none",
          },
          audio: false,
        },
        {
          video: {
            facingMode: facingMode,
            width: { ideal: 1280 },
            height: { ideal: 720 },
            resizeMode: "none",
          },
          audio: false,
        },
        {
          video: { facingMode: facingMode },
          audio: false,
        },
        {
          video: true,
          audio: false,
        },
      ];

      let stream = null;
      let lastError = null;

      // Try constraints in order of preference
      for (let i = 0; i < constraints.length; i++) {
        try {
          const timeoutPromise = new Promise((_, reject) => {
            setTimeout(
              () => reject(new Error("Camera request timeout")),
              10000,
            );
          });

          const streamPromise = navigator.mediaDevices.getUserMedia(
            constraints[i],
          );
          stream = (await Promise.race([
            streamPromise,
            timeoutPromise,
          ])) as MediaStream;
          break;
        } catch (error) {
          lastError = error;
          if (i === constraints.length - 1) {
            throw lastError;
          }
        }
      }

      if (!stream) {
        throw new Error("Failed to obtain camera stream");
      }
      if (!isMountedRef.current || requestId !== startRequestIdRef.current) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }

      streamRef.current = stream;

      // Set up video with enhanced mobile support
      if (videoRef.current) {
        resetZoomToDefault();
        videoRef.current.srcObject = stream;
        videoRef.current.autoplay = true;
        videoRef.current.playsInline = true;
        videoRef.current.muted = true;

        // Wait for metadata to load
        await new Promise((resolve, reject) => {
          const timeout = setTimeout(
            () => reject(new Error("Video metadata timeout")),
            5000,
          );

          videoRef.current!.onloadedmetadata = () => {
            clearTimeout(timeout);
            resolve(void 0);
          };

          videoRef.current!.onerror = (error) => {
            clearTimeout(timeout);
            reject(error);
          };
        });

        // Force play for mobile
        try {
          await videoRef.current.play();
          if (!isMountedRef.current || requestId !== startRequestIdRef.current) {
            stream.getTracks().forEach((track) => track.stop());
            return;
          }
          setStreamReady(true);
          // Detect zoom capabilities after stream is ready
          detectZoomCapabilities();
          // Force native camera zoom back to 1x on devices that support it.
          const videoTrack = stream.getVideoTracks()[0];
          if (videoTrack) {
            const capabilities = videoTrack.getCapabilities() as ZoomCapabilities;
            if (capabilities.zoom) {
              await videoTrack
                .applyConstraints({
                  advanced: [{ zoom: 1.0 } as ZoomConstraint],
                })
                .catch((err) => {
                  console.warn("Failed to reset native zoom to 1x:", err);
                });
            }
          }
        } catch (playError) {
          console.warn("Autoplay failed, but continuing:", playError);
          if (!isMountedRef.current || requestId !== startRequestIdRef.current) {
            stream.getTracks().forEach((track) => track.stop());
            return;
          }
          setStreamReady(true);
          // Detect zoom capabilities even if autoplay fails
          detectZoomCapabilities();
        }
      }

      if (!isMountedRef.current || requestId !== startRequestIdRef.current) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }
      setHasStarted(true);
    } catch (cause) {
      const error = cause instanceof Error ? cause : new Error(String(cause));
      if (!isMountedRef.current || requestId !== startRequestIdRef.current) {
        return;
      }
      console.error("Camera error:", error);
      let message = "Failed to access camera. ";

      if (error.name === "NotAllowedError") {
        message =
          "Camera permission denied. Please allow camera access and try again.";
      } else if (error.name === "NotFoundError") {
        message = "No camera found. Please connect a camera and try again.";
      } else if (error.name === "NotReadableError") {
        message = "Camera is in use by another application.";
      } else if (error.message?.includes("timeout")) {
        message = "Camera request timed out. Please try again.";
      } else {
        message += error.message || "Please check your camera settings.";
      }

      setCameraError(message);
      setStreamReady(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facingMode, isFullscreen, stopActiveStream]);

  // Flip camera
  const flipCamera = useCallback(() => {
    setFacingMode((prev) => (prev === "user" ? "environment" : "user"));
  }, []);

  // Detect zoom capabilities from the video track
  const detectZoomCapabilities = useCallback(() => {
    if (!streamRef.current) return;

    const videoTrack = streamRef.current.getVideoTracks()[0];
    if (!videoTrack) return;

    try {
      const capabilities = videoTrack.getCapabilities() as ZoomCapabilities;
      if (capabilities.zoom) {
        setZoomCapabilities({
          min: capabilities.zoom.min || 1.0,
          max: capabilities.zoom.max || 3.0,
          supported: true,
        });
        console.log("Zoom supported:", capabilities.zoom);
      } else {
        console.log("Zoom not supported by device, using CSS fallback");
      }
    } catch (error) {
      console.warn("Could not detect zoom capabilities:", error);
    }
  }, []);

  // Keep every new camera session at a neutral (1x) zoom level.
  const resetZoomToDefault = useCallback(() => {
    setZoom(1.0);
    lastZoomRef.current = 1.0;

    if (videoRef.current) {
      // Remove fallback CSS zoom from previous interactions.
      videoRef.current.style.transform = "";
    }
  }, []);

  // Apply zoom to video track or CSS
  const applyZoom = useCallback(
    (zoomLevel: number) => {
      if (!streamRef.current || !videoRef.current) return;

      const videoTrack = streamRef.current.getVideoTracks()[0];
      if (!videoTrack) return;

      // Clamp zoom level
      const clampedZoom = Math.max(
        zoomCapabilities.min,
        Math.min(zoomCapabilities.max, zoomLevel),
      );

      setZoom(clampedZoom);
      lastZoomRef.current = clampedZoom;

      // Clear existing timeout
      if (zoomTimeoutRef.current) {
        clearTimeout(zoomTimeoutRef.current);
      }

      // Debounce zoom application
      zoomTimeoutRef.current = setTimeout(() => {
        if (zoomCapabilities.supported) {
          // Use MediaStream API zoom
          videoTrack
            .applyConstraints({
              advanced: [{ zoom: clampedZoom } as ZoomConstraint],
            })
            .catch((err) => {
              console.warn("Failed to apply zoom constraint:", err);
            });
        } else {
          // Fallback to CSS transform
          if (videoRef.current) {
            videoRef.current.style.transform = `scale(${clampedZoom})`;
          }
        }
      }, 50);
    },
    [zoomCapabilities],
  );

  // Touch gesture handlers for pinch-to-zoom
  const handleTouchStart = useCallback((e: TouchEvent) => {
    if (e.touches.length === 2) {
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const distance = Math.hypot(
        touch2.clientX - touch1.clientX,
        touch2.clientY - touch1.clientY,
      );
      touchStartDistanceRef.current = distance;
      pinchStartZoomRef.current = lastZoomRef.current;
    }
  }, []);

  const handleTouchMove = useCallback(
    (e: TouchEvent) => {
      if (e.touches.length === 2 && touchStartDistanceRef.current) {
        e.preventDefault(); // Prevent default pinch zoom

        const touch1 = e.touches[0];
        const touch2 = e.touches[1];
        const distance = Math.hypot(
          touch2.clientX - touch1.clientX,
          touch2.clientY - touch1.clientY,
        );

        const scale = distance / touchStartDistanceRef.current;
        const newZoom = pinchStartZoomRef.current * scale;

        applyZoom(newZoom);
      }
    },
    [applyZoom],
  );

  const handleTouchEnd = useCallback(() => {
    if (touchStartDistanceRef.current) {
      touchStartDistanceRef.current = null;
    }
    pinchStartZoomRef.current = lastZoomRef.current;
  }, []);




  // Handle facing mode changes - restart camera when facing mode changes
  useEffect(() => {
    if (hasStarted) {
      // Reset zoom when switching cameras
      setZoom(1.0);
      lastZoomRef.current = 1.0;
      startCamera();
    }
  }, [facingMode, hasStarted, startCamera]);

  // Attach touch event listeners for pinch-to-zoom on mobile
  useEffect(() => {
    const videoElement = videoRef.current;
    if (!videoElement || !isMobile) return;

    videoElement.addEventListener("touchstart", handleTouchStart, {
      passive: false,
    });
    videoElement.addEventListener("touchmove", handleTouchMove, {
      passive: false,
    });
    videoElement.addEventListener("touchend", handleTouchEnd);

    return () => {
      videoElement.removeEventListener("touchstart", handleTouchStart);
      videoElement.removeEventListener("touchmove", handleTouchMove);
      videoElement.removeEventListener("touchend", handleTouchEnd);
    };
  }, [isMobile, handleTouchStart, handleTouchMove, handleTouchEnd]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      startRequestIdRef.current += 1;
      stopActiveStream();
      if (countdownRef.current) {
        clearTimeout(countdownRef.current);
      }
    };
  }, [stopActiveStream]);

  // Auto-start camera on mount if autoStart is true
  useEffect(() => {
    if (autoStart && !hasStarted && !cameraError) {
      startCamera();
    }
  }, [autoStart, hasStarted, cameraError, startCamera]);

  // If not started yet, show start button
  if (!hasStarted && !cameraError) {
    return (
      <Card className={cn("border border-white/40 bg-white/40 backdrop-blur-xl shadow-[0_8px_30px_rgb(22,107,147,0.06)] rounded-[2rem] overflow-hidden relative group", className)}>
        <div className="absolute inset-0 bg-gradient-to-br from-[#166B93]/5 to-transparent pointer-events-none" />
        <CardContent className="p-8 sm:p-10 text-center relative z-10 flex flex-col items-center justify-center min-h-[300px]">
          <div className="w-20 h-20 bg-gradient-to-br from-blue-100 to-blue-50 rounded-[1.5rem] flex items-center justify-center mb-6 shadow-[0_8px_30px_rgb(22,107,147,0.08)] group-hover:scale-105 transition-transform duration-300">
            <Camera className="w-10 h-10 text-jema-blue" />
          </div>
          <h3 className="text-2xl font-bold mb-3 font-nunito tracking-tight text-gray-900"><Trans>Ready to Take Photos</Trans></h3>
          <p className="text-gray-600 mb-8 font-jakarta leading-relaxed"><Trans>Click below to start your camera securely</Trans></p>
          <div className="w-full max-w-[280px]">
            <Button
              onClick={startCamera}
              className="w-full h-14 rounded-2xl text-lg font-bold shadow-lg hover:shadow-xl transition-all"
              variant="jema"
              type="button"
            >
              <Camera className="w-5 h-5 mr-3" />
              <Trans>Start Camera</Trans>
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Error state
  if (cameraError) {
    return (
      <Card className={cn("border border-white/40 bg-white/40 backdrop-blur-xl shadow-[0_8px_30px_rgb(147,22,22,0.06)] rounded-[2rem] overflow-hidden relative group", className)}>
         <div className="absolute inset-0 bg-gradient-to-br from-red-500/5 to-transparent pointer-events-none" />
        <CardContent className="p-8 text-center relative z-10">
          <div className="w-16 h-16 bg-red-100 rounded-[1.25rem] flex items-center justify-center mx-auto mb-5 shadow-sm">
            <AlertCircle className="w-8 h-8 text-red-500" />
          </div>
          <h3 className="text-xl font-bold mb-2 font-nunito"><Trans>Camera Error</Trans></h3>
          <p className="text-gray-600 mb-6 font-jakarta text-sm">{cameraError}</p>
          <div className="space-y-3 max-w-[280px] mx-auto">
            <Button onClick={startCamera} className="w-full h-12 rounded-xl font-bold bg-red-50 text-red-600 border border-red-200 hover:bg-red-100 shadow-sm" type="button" variant="outline">
              <Camera className="w-4 h-4 mr-2" />
              <Trans>Try Again</Trans>
            </Button>
            <Button
              onClick={() => {
                setHasStarted(false);
                setCameraError(null);
                setStreamReady(false);
              }}
              variant="outline"
              className="w-full h-12 rounded-xl font-medium border-gray-200 text-gray-600 hover:bg-gray-50"
            >
              <Trans>Reset</Trans>
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Fullscreen mode — portal to document.body so `position:fixed` is not trapped by
  // ancestors with transform (e.g. intake v2 CardShell card-enter-*), which caused the
  // preview to sit in one column with empty space beside it on desktop.
  if (isFullscreen && typeof document !== "undefined") {
    const fullscreenEl = (
      <div
        ref={(el) => {
          if (el && isFullscreen) {
            const mobile = /Mobi|Android/i.test(navigator.userAgent);

            if (mobile) {
              if (el.requestFullscreen) {
                el.requestFullscreen().catch(() => {});
              } else if (
                (el as unknown as { webkitRequestFullscreen?: () => void }).webkitRequestFullscreen
              ) {
                (el as unknown as { webkitRequestFullscreen: () => void }).webkitRequestFullscreen();
              } else if ((el as unknown as { msRequestFullscreen?: () => void }).msRequestFullscreen) {
                (el as unknown as { msRequestFullscreen: () => void }).msRequestFullscreen();
              }
            }
          }
        }}
        className="fixed inset-0 left-0 top-0 flex h-[100dvh] w-[100dvw] flex-col overflow-hidden bg-black"
        style={{ zIndex: 999999 }}
      >
        {/* Top Status Bar */}
        <div className="absolute top-0 left-0 right-0 z-30 safe-area-top">
          <div className="bg-gradient-to-b from-black/90 via-black/60 to-transparent backdrop-blur-sm">
            <div className="flex items-center justify-between px-4 py-3">
              {/* Left: Instructions */}
              <div className="flex-1 min-w-0">
                {instructions ? (
                  <div className="max-w-sm">
                    <h3 className="font-semibold text-sm text-white truncate leading-tight">
                      {instructions.title}
                    </h3>
                    <p className="text-xs text-gray-300 truncate mt-0.5 leading-tight">
                      {instructions.description}
                    </p>
                  </div>
                ) : (
                  <div className="text-white">
                    <p className="text-sm font-medium"><Trans>Camera</Trans></p>
                  </div>
                )}
              </div>

              {/* Right: Action buttons */}
              <div className="flex gap-2 ml-4 shrink-0">
                {onToggleFullscreen && (
                  <Button
                    onClick={onToggleFullscreen}
                    variant="ghost"
                    size="sm"
                    className="text-white hover:bg-white/20 active:bg-white/30 bg-black/40 backdrop-blur-sm h-10 w-10 p-0 rounded-full border border-white/20 transition-all duration-200"
                  >
                    <Minimize2 className="w-4 h-4" />
                  </Button>
                )}
                {onClose && (
                  <Button
                    onClick={onClose}
                    variant="ghost"
                    size="sm"
                    className="text-white hover:bg-white/20 active:bg-white/30 bg-black/40 backdrop-blur-sm h-10 w-10 p-0 rounded-full border border-white/20 transition-all duration-200"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="absolute inset-0 flex items-center justify-center bg-black">
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className={cn(
              "max-h-full max-w-full object-contain",
              facingMode === "user" && "scale-x-[-1]",
            )}
          />
        </div>

        {/* Example Image Overlay */}
        {instructions?.example && !countdown && streamReady && (
          <div className="absolute top-20 right-4 z-20 animate-in fade-in duration-500">
            <div className="bg-black/90 rounded-2xl p-3 backdrop-blur-lg border border-white/30 shadow-2xl">
              <img
                src={instructions.example}
                alt="Example position"
                className="w-24 h-24 object-cover rounded-xl border-2 border-white/50 shadow-lg"
              />
              <p className="text-white text-xs text-center mt-2 font-medium opacity-90">
                <Trans>Example</Trans>
              </p>
            </div>
          </div>
        )}

        {/* Countdown Overlay */}
        {countdown && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/60 backdrop-blur-sm z-30">
            <div className="text-center">
              <div className="relative">
                <div className="text-white text-9xl font-bold animate-pulse mb-4 drop-shadow-2xl">
                  {countdown}
                </div>
                <div className="absolute inset-0 text-blue-400 text-9xl font-bold animate-ping opacity-30">
                  {countdown}
                </div>
              </div>
              <div className="bg-white/20 backdrop-blur-sm rounded-full px-6 py-2 border border-white/30">
                <p className="text-white text-lg font-medium"><Trans>Get ready...</Trans></p>
              </div>
            </div>
          </div>
        )}

        {/* Loading Overlay */}
        {!streamReady && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/80 backdrop-blur-sm z-40">
            <div className="text-white text-center">
              <div className="relative mb-6">
                <div className="animate-spin rounded-full h-16 w-16 border-4 border-white/30 border-t-white mx-auto"></div>
                <div className="absolute inset-0 animate-ping rounded-full h-16 w-16 border-2 border-white/20 mx-auto"></div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl px-6 py-4 border border-white/20">
                <p className="text-lg font-medium mb-1"><Trans>Loading camera...</Trans></p>
                <p className="text-sm text-gray-300"><Trans>Please wait a moment</Trans></p>
              </div>
            </div>
          </div>
        )}

        {/* Zoom Level Indicator */}
        {zoom > 1.0 && streamReady && (
          <div className="absolute bottom-24 left-1/2 -translate-x-1/2 z-40 animate-in fade-in duration-200">
            <div className="bg-black/80 backdrop-blur-sm rounded-full px-4 py-2 border border-white/30 shadow-lg">
              <div className="flex items-center gap-2">
                <ZoomIn className="w-4 h-4 text-white" />
                <p className="text-white text-sm font-medium">
                  {zoom.toFixed(1)}x
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Bottom Controls */}
        <div className="absolute bottom-0 left-0 right-0 z-50">
          <div className="bg-gradient-to-t from-black/90 via-black/60 to-transparent backdrop-blur-sm">
            <div className="flex items-center justify-center px-6 py-6 min-h-[100px]">
              <div className="flex items-center justify-between w-full max-w-md">
                {/* Left: Flip Camera */}
                <Button
                  onClick={flipCamera}
                  variant="ghost"
                  size="lg"
                  className="text-white hover:bg-white/20 active:bg-white/30 bg-black/40 backdrop-blur-sm w-12 h-12 rounded-full p-0 border border-white/20 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                  disabled={!streamReady}
                >
                  <SwitchCamera className="w-5 h-5" />
                </Button>

                {/* Center: Capture Button */}
                <div className="relative flex-shrink-0 mx-4">
                  {/* Capture Button - Always clickable for debugging */}
                  <button
                    onClick={(e) => {
                      console.log("CAPTURE BUTTON CLICKED!", {
                        isCapturing,
                        countdown,
                        streamReady,
                        disabled: isCapturing || !!countdown || !streamReady,
                      });

                      // Force capture even if disabled for debugging
                      if (streamReady) {
                        handleCapture(e);
                      } else {
                        console.log("Stream not ready, cannot capture");
                      }
                    }}
                    className={cn(
                      "bg-white hover:bg-gray-100 active:bg-gray-200 text-black w-16 h-16 sm:w-18 sm:h-18 rounded-full flex items-center justify-center transition-all duration-200 shadow-2xl border-4 border-white/50",
                      (isCapturing || countdown) && "animate-pulse",
                      (isCapturing || !!countdown || !streamReady) &&
                        "opacity-50 cursor-not-allowed",
                    )}
                    type="button"
                    style={{
                      touchAction: "manipulation",
                      pointerEvents: "auto",
                      zIndex: 9999,
                      position: "relative",
                    }}
                  >
                    {isCapturing ? (
                      <div className="animate-spin rounded-full h-6 w-6 sm:h-7 sm:w-7 border-2 border-gray-600 border-t-transparent"></div>
                    ) : autoCapture && !countdown ? (
                      <Timer className="w-7 h-7 sm:w-8 sm:h-8" />
                    ) : (
                      <Camera className="w-7 h-7 sm:w-8 sm:h-8" />
                    )}
                  </button>

                  {/* Capture button ring animation */}
                  {streamReady && !countdown && (
                    <div className="absolute inset-0 rounded-full border-2 border-white/30 animate-ping pointer-events-none"></div>
                  )}
                </div>

                {/* Right: Status Indicator */}
                <div className="w-12 h-12 flex flex-col items-center justify-center flex-shrink-0">
                  {streamReady ? (
                    <div className="relative">
                      <div className="w-3 h-3 bg-green-400 rounded-full shadow-lg"></div>
                      <div className="absolute inset-0 w-3 h-3 bg-green-400 rounded-full animate-ping opacity-30"></div>
                    </div>
                  ) : (
                    <div className="w-3 h-3 bg-red-400 rounded-full opacity-60 animate-pulse"></div>
                  )}
                  {/* Debug info */}
                  <div className="text-white text-xs mt-1">
                    {isCapturing
                      ? "CAP"
                      : countdown
                        ? "CD"
                        : streamReady
                          ? "RDY"
                          : "WAIT"}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <canvas ref={canvasRef} className="hidden" />
      </div>
    );

    return createPortal(fullscreenEl, document.body);
  }

  // Regular camera view
  const isDialog = dialogLayout && !isFullscreen;

  const preview = (
    <div
      className={cn(
        "relative overflow-hidden bg-gray-950",
        isDialog
          ? "aspect-video w-full max-h-[min(48vh,22rem)] sm:max-h-[min(52vh,24rem)] rounded-2xl ring-1 ring-gray-200/90 shadow-inner"
          : cn(
              "rounded-lg bg-gray-100",
              isMobile ? "aspect-[3/4]" : "aspect-video",
            ),
      )}
    >
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        className={cn(
          "h-full w-full object-cover",
          facingMode === "user" && "scale-x-[-1]",
        )}
      />

      {instructions?.example && !countdown && (
        <div className="absolute top-2 right-2 z-10">
          <div className="rounded-lg border border-white/20 bg-black/80 p-2 backdrop-blur-sm">
            <img
              src={instructions.example}
              alt="Example position"
              className="h-16 w-16 rounded border border-white/50 object-cover"
            />
            <p className="mt-1 text-center text-xs font-medium text-white">
              <Trans>Example</Trans>
            </p>
          </div>
        </div>
      )}

      {countdown && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/50">
          <div className="animate-pulse text-6xl font-bold text-white">
            {countdown}
          </div>
        </div>
      )}

      {onToggleFullscreen && !isDialog && (
        <div className="absolute top-2 left-2 z-10">
          <Button
            onClick={onToggleFullscreen}
            variant="secondary"
            size="sm"
            className="bg-white/90 shadow-lg hover:bg-white"
            disabled={!streamReady}
          >
            <Maximize2 className="h-4 w-4" />
          </Button>
        </div>
      )}

      {isDialog && streamReady && (
        <div className="absolute bottom-3 left-3 z-20">
          <Button
            type="button"
            onClick={flipCamera}
            variant="secondary"
            size="icon"
            className="h-11 w-11 rounded-full border border-white/25 bg-white/95 shadow-lg backdrop-blur-sm hover:bg-white"
            aria-label={i18n._(t`Flip camera`)}
          >
            <SwitchCamera className="h-5 w-5 text-gray-800" />
          </Button>
        </div>
      )}

      {!streamReady &&
        (isDialog ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-b from-gray-900/85 to-gray-950/95 px-4">
            <div className="mb-3 h-9 w-9 animate-spin rounded-full border-2 border-white/25 border-t-white" />
            <p className="text-sm font-medium text-white">
              <Trans>Loading camera...</Trans>
            </p>
            <p className="mt-1 max-w-xs text-center text-xs text-white/65">
              <Trans>
                Allow access when prompted. If you use a virtual camera, start it before continuing.
              </Trans>
            </p>
          </div>
        ) : (
          <div className="absolute inset-0 flex items-center justify-center bg-black/50">
            <div className="text-center text-white">
              <div className="mx-auto mb-2 h-8 w-8 animate-spin rounded-full border-b-2 border-white" />
              <p className="text-sm">
                <Trans>Loading camera...</Trans>
              </p>
            </div>
          </div>
        ))}
    </div>
  );

  const controls = (
    <div
      className={cn(
        "[&_button]:!my-0",
        isDialog ? "mt-4 flex flex-col gap-3 px-0.5" : "mt-4 flex flex-col gap-2.5",
      )}
    >
      {!isDialog && (
        <Button
          onClick={flipCamera}
          variant="outline"
          size="xl"
          className="h-12 w-full rounded-xl border-gray-200 bg-white font-medium text-gray-700 hover:bg-gray-50"
          disabled={!streamReady}
        >
          <SwitchCamera className="h-5 w-5 shrink-0" />
          <Trans>Flip Camera</Trans>
        </Button>
      )}

      <Button
        onClick={handleCapture}
        size="xl"
        className={cn(
          "h-12 rounded-xl font-semibold shadow-md transition-all hover:shadow-lg",
          isDialog
            ? "w-full bg-gradient-to-r from-[#166B93] to-[#1a7faf] text-white hover:from-[#135a7c] hover:to-[#166B93]"
            : "w-full",
        )}
        disabled={isCapturing || !!countdown || !streamReady || disabled}
        type="button"
      >
        {autoCapture && !countdown ? (
          <>
            <Timer className="h-5 w-5 shrink-0" />
            <Trans>Start Timer ({captureDelay}s)</Trans>
          </>
        ) : (
          <>
            <Camera className="h-5 w-5 shrink-0" />
            {isCapturing ? <Trans>Capturing...</Trans> : <Trans>Take Photo</Trans>}
          </>
        )}
      </Button>

      {onToggleFullscreen && !isMobile && !isDialog && (
        <Button
          onClick={onToggleFullscreen}
          variant="outline"
          size="sm"
          disabled={!streamReady}
          className="self-end"
        >
          <Maximize2 className="h-4 w-4" />
        </Button>
      )}
    </div>
  );

  if (isDialog) {
    return (
      <div className={cn("w-full", className)}>
        {preview}
        {controls}
        <canvas ref={canvasRef} className="hidden" />
      </div>
    );
  }

  return (
    <Card className={className}>
      <CardContent className="p-4">
        {preview}
        {controls}
        <canvas ref={canvasRef} className="hidden" />
      </CardContent>
    </Card>
  );
}
